import React, { useState, useEffect } from 'react';
import type { User } from '@supabase/supabase-js';
import type { CurrentPage } from '../App'; // Import CurrentPage type
import { Calendar, FileText, Settings, MessageSquare, Folder, Wrench, Building2, Users, MessageCircle, Mail } from 'lucide-react';
import logoPath from '../assets/NOOR.png'; // Import the logo image
import AppLifecycleIndicator from './AppLifecycleIndicator';
import ContactUsModal from './ContactUsModal';
import { getUserOrganization, getUserRole } from '../services/teamService';
import { chatNotificationService } from '../services/chatNotificationService';
import { ROLE_METADATA } from '../types/team';
import type { UserRole } from '../types/team';

interface HomeScreenProps {
  currentUser: User | null; // Expect the full user object
  onNavigate: (page: CurrentPage) => void;
}

// Removed AiIcon as it wasn't being used in the latest code structure

const HomeScreen: React.FC<HomeScreenProps> = ({ currentUser, onNavigate }) => {
  // Extract first name here
  const firstName = currentUser?.user_metadata?.first_name || 'User';
  const subscriptionPlan = currentUser?.user_metadata?.subscription_plan;
  const accountType = currentUser?.user_metadata?.account_type || 'personal';
  const companyName = currentUser?.user_metadata?.company_name;
  
  // State for organization name (for team members)
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isLoadingRole, setIsLoadingRole] = useState(true);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  // Fetch organization name and user role (non-blocking)
  useEffect(() => {
    // IMMEDIATELY show menu - don't block on organization lookup
    setIsLoadingRole(false);
    
    const loadOrganizationAndRole = async () => {
      console.log('[HomeScreen] Loading organization and role in background...');
      
      try {
        // Set aggressive 2-second timeout for each query
        const timeoutPromise = new Promise<null>((resolve) => {
          setTimeout(() => {
            console.warn('[HomeScreen] Organization lookup timed out after 2 seconds');
            resolve(null);
          }, 2000);
        });
        
        // Race the queries against timeout
        const [org, role] = await Promise.all([
          Promise.race([getUserOrganization(), timeoutPromise]),
          Promise.race([getUserRole(), timeoutPromise])
        ]);
        
        if (org) {
          console.log('[HomeScreen] ✅ Found organization:', org.name || org.company_name);
          setOrganizationName(org.name || org.company_name || null);
        } else {
          console.log('[HomeScreen] No organization found for user');
        }
        
        if (role) {
          console.log('[HomeScreen] ✅ User role:', role);
        }
        setUserRole(role);
      } catch (error) {
        console.error('[HomeScreen] Error loading organization:', error);
      }
    };
    
    // Load in background without blocking UI
    loadOrganizationAndRole();
  }, [currentUser?.id]); // Re-run when user changes (including on component remount)

  // Load unread chat count
  useEffect(() => {
    chatNotificationService.initialize();
    setUnreadChatCount(chatNotificationService.getUnreadCount());

    const handleNewMessage = () => {
      setUnreadChatCount(chatNotificationService.getUnreadCount());
    };

    chatNotificationService.addListener(handleNewMessage);

    return () => {
      chatNotificationService.removeListener(handleNewMessage);
    };
  }, []);
  
  // Format plan name for display
  const getPlanDisplayName = (plan: string | undefined): string => {
    if (!plan) return '';
    const planNames: Record<string, string> = {
      'standard': 'Standard',
      'pro': 'Pro',
      'pro_trial': 'Pro Trial',
      'team': 'Team',
      'business': 'Business'
    };
    return planNames[plan] || plan;
  };

  // Base menu items
  const baseMenuItems = [
    { 
      name: 'Calendar', 
      icon: Calendar, 
      page: 'calendar' as CurrentPage['page'],
      description: 'Schedule and manage energy assessments and estate agency appointments'
    },
    { 
      name: 'Chat', 
      icon: MessageSquare, 
      page: 'chatHub' as CurrentPage['page'],
      description: 'AI chat assistant and team chat collaboration tools'
    },
    { 
      name: 'Estate Agency Hub', 
      icon: Building2, 
      page: 'estateAgency' as CurrentPage['page'], 
      description: 'Access property inventory documentation and floor plan tools for estate agency workflows'
    },
    { 
      name: 'Energy Hub', 
      icon: Wrench, 
      page: 'retrofit' as CurrentPage['page'],
      description: 'Comprehensive energy assessment tools including floor plans and retrofit surveys'
    },
    { 
      name: 'My Files', 
      icon: Folder, 
      page: 'myFiles' as CurrentPage['page'],
      description: 'Manage, sync, and organize your floor plans and property inventories across devices'
    },
    { 
      name: 'Settings', 
      icon: Settings, 
      page: 'settings' as CurrentPage['page'],
      description: 'Configure user profile, assessor credentials, and application preferences'
    },
  ];

  // Filter menu items based on user role
  let menuItems = baseMenuItems;
  
  // Add Team Management for business accounts (but not for team_admin)
  if (accountType === 'business' && (subscriptionPlan === 'team' || subscriptionPlan === 'business') && userRole !== 'team_admin') {
    menuItems = [
      ...baseMenuItems.slice(0, 3),
      {
        name: 'Team Management',
        icon: Users,
        page: 'team-management' as CurrentPage['page'],
        description: 'Manage team members, send invitations, and control access to your organization'
      },
      ...baseMenuItems.slice(3)
    ];
  }
  
  // Team admins only have access to Calendar, Chat (team chat), and Settings
  if (userRole === 'team_admin') {
    menuItems = baseMenuItems.filter(item => 
      item.name === 'Calendar' || 
      item.name === 'Chat' || 
      item.name === 'Settings'
    );
  }

  // Define gradient themes for each menu item
  const gradientThemes: Record<string, { 
    from: string; 
    to: string; 
    iconBg: string; 
    iconFrom: string; 
    iconTo: string;
    hoverFrom: string;
    hoverTo: string;
  }> = {
    'Calendar': {
      from: 'from-amber-50',
      to: 'to-orange-50',
      iconBg: 'bg-gradient-to-r from-amber-500 to-orange-600',
      iconFrom: 'from-amber-500',
      iconTo: 'to-orange-600',
      hoverFrom: 'hover:from-amber-100',
      hoverTo: 'hover:to-orange-100'
    },
    'Estate Agency Hub': {
      from: 'from-sky-50/40',
      to: 'to-blue-50/40',
      iconBg: 'bg-gradient-to-r from-blue-500 to-indigo-600',
      iconFrom: 'from-blue-500',
      iconTo: 'to-indigo-600',
      hoverFrom: 'hover:from-sky-100',
      hoverTo: 'hover:to-blue-100'
    },
    'Energy Hub': {
      from: 'from-emerald-50',
      to: 'to-teal-50',
      iconBg: 'bg-gradient-to-r from-emerald-500 to-teal-600',
      iconFrom: 'from-emerald-500',
      iconTo: 'to-teal-600',
      hoverFrom: 'hover:from-emerald-100',
      hoverTo: 'hover:to-teal-100'
    },
    'My Files': {
      from: 'from-purple-50',
      to: 'to-violet-50',
      iconBg: 'bg-gradient-to-r from-purple-500 to-violet-600',
      iconFrom: 'from-purple-500',
      iconTo: 'to-violet-600',
      hoverFrom: 'hover:from-purple-100',
      hoverTo: 'hover:to-violet-100'
    },
    'Chat': {
      from: 'from-cyan-50',
      to: 'to-sky-50',
      iconBg: 'bg-gradient-to-r from-cyan-500 to-sky-600',
      iconFrom: 'from-cyan-500',
      iconTo: 'to-sky-600',
      hoverFrom: 'hover:from-cyan-100',
      hoverTo: 'hover:to-sky-100'
    },
    'Team Management': {
      from: 'from-indigo-50',
      to: 'to-purple-50',
      iconBg: 'bg-gradient-to-r from-indigo-500 to-purple-600',
      iconFrom: 'from-indigo-500',
      iconTo: 'to-purple-600',
      hoverFrom: 'hover:from-indigo-100',
      hoverTo: 'hover:to-purple-100'
    },
    'Settings': {
      from: 'from-slate-50',
      to: 'to-gray-50',
      iconBg: 'bg-gradient-to-r from-slate-600 to-gray-700',
      iconFrom: 'from-slate-600',
      iconTo: 'to-gray-700',
      hoverFrom: 'hover:from-slate-100',
      hoverTo: 'hover:to-gray-100'
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col">
       {/* Compact Premium Header - Fixed/Sticky */}
       <header className="sticky top-0 z-50 w-full mb-6 sm:mb-8 px-4 pt-6 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-slate-50" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))', paddingLeft: 'max(1rem, env(safe-area-inset-left))', paddingRight: 'max(1rem, env(safe-area-inset-right))' }}>
         {/* Compact User Info Card with Integrated Logo */}
         <div className="relative overflow-hidden bg-white rounded-2xl shadow-lg border border-slate-200/60 p-4 sm:p-5">
           {/* Subtle gradient background decoration */}
           <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/30 via-purple-50/15 to-pink-50/30 pointer-events-none"></div>
           
           <div className="relative z-10">
             {/* Top Row: Logo + Welcome Message */}
             <div className="flex items-center justify-between gap-4 mb-3">
               <div className="flex-1 min-w-0">
                 <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-slate-900 via-indigo-900 to-purple-900 bg-clip-text text-transparent truncate">
                   Welcome back, {firstName}!
                 </h1>
                 <p className="text-slate-600 text-sm sm:text-base font-medium mt-0.5">
                   Let's get started with your work today.
                 </p>
               </div>
               
               {/* Integrated Logo */}
               <img
                  src={logoPath}
                  alt="NOOR Logo"
                  className="h-8 sm:h-10 w-auto flex-shrink-0 drop-shadow-md"
               />
             </div>

             {/* Account Info Row - Compact Badges */}
             <div className="flex flex-wrap items-center gap-2">
               {/* Plan Badge */}
               {subscriptionPlan && (
                 <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 shadow-md shadow-emerald-500/20">
                   <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                   <span className="text-white font-bold text-xs tracking-wide">
                     {getPlanDisplayName(subscriptionPlan)} Plan
                   </span>
                 </div>
               )}

               {/* Account Type Badge */}
               {accountType === 'business' && (
                 <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200">
                   <Building2 size={13} className="text-purple-700" />
                   <span className="text-purple-800 font-semibold text-xs">
                     Business
                   </span>
                 </div>
               )}

               {/* Organization/Company Name Badge - Shows organization name for team members */}
              {(organizationName || companyName) && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200">
                  <Building2 size={13} className="text-indigo-600" />
                  <span className="text-slate-800 font-semibold text-xs">
                    {organizationName || companyName}
                  </span>
                </div>
              )}
               
               {/* Subtle divider dot + text */}
               <div className="ml-auto flex items-center gap-1.5 text-slate-500 text-xs">
                 <span className="w-1 h-1 bg-indigo-500 rounded-full"></span>
                 <span className="hidden sm:inline">Select an option below</span>
               </div>
             </div>

             {/* Contact/Bug Report Banner */}
             <div className="mt-4 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 border-2 border-purple-200 rounded-xl p-3 shadow-sm hover:shadow-md transition-all duration-300">
               <div className="flex items-start gap-2.5">
                 <div className="p-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg shadow-md flex-shrink-0">
                   <MessageCircle className="text-white" size={16} />
                 </div>
                 <div className="flex-1 min-w-0">
                   <h3 className="text-xs font-bold text-purple-900 mb-0.5">Need Help or Found a Bug?</h3>
                   <p className="text-xs text-purple-800 mb-2">
                     Report issues, request features, or get support.
                   </p>
                   <button
                    onClick={() => setIsContactModalOpen(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <Mail size={12} />
                    Contact Support
                  </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       </header>

       {/* Modern Grid Menu - Sophisticated Gradient Cards */}
       <main className="w-full max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8 flex-grow px-4 pb-6 sm:px-6 lg:px-8" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))', paddingLeft: 'max(1rem, env(safe-area-inset-left))', paddingRight: 'max(1rem, env(safe-area-inset-right))' }}>
        {isLoadingRole ? (
          <div className="col-span-full flex items-center justify-center py-20">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mb-4"></div>
              <p className="text-slate-600">Loading menu...</p>
            </div>
          </div>
        ) : (
          menuItems.map((item) => {
          const theme = gradientThemes[item.name];
          return (
            <button
              key={item.name}
              onClick={(e) => {
                e.currentTarget.blur(); // Immediately blur after click to prevent focus state
                onNavigate({ page: item.page } as CurrentPage);
              }}
              onTouchEnd={(e) => {
                e.currentTarget.blur(); // Also blur on touch end for mobile
              }}
              className={`
                relative overflow-hidden
                bg-gradient-to-br ${theme.from} via-white ${theme.to}
                border border-slate-200/60
                rounded-2xl p-6 sm:p-7 lg:p-8
                flex flex-col items-center justify-center text-center
                shadow-lg hover:shadow-2xl
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-400
                transform hover:scale-[1.04] active:scale-[0.98]
                transition-all duration-300 ease-out
                group
                ${theme.hoverFrom} ${theme.hoverTo}
                [&:focus:not(:focus-visible)]:outline-none [&:focus:not(:focus-visible)]:ring-0
              `}
            >
              {/* Decorative gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              
              {/* Icon Container with Gradient Background */}
              <div className={`
                relative
                ${theme.iconBg}
                rounded-2xl p-4 sm:p-5
                mb-5 sm:mb-6
                shadow-lg
                backdrop-blur-sm
                transform group-hover:scale-110 group-hover:rotate-3
                transition-all duration-300
              `}>
                <item.icon 
                  className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white drop-shadow-md" 
                  strokeWidth={2} 
                />
              </div>

              {/* Title with enhanced typography */}
              <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4">
                <span className="text-base sm:text-lg lg:text-xl font-bold text-slate-800 transition-colors duration-300 text-center leading-tight drop-shadow-sm">
                  {item.name}
                </span>
                {item.name === 'Chat' && unreadChatCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                    {unreadChatCount > 99 ? '99+' : unreadChatCount}
                  </span>
                )}
              </div>

              {/* Description with modern styling */}
              <div className="max-w-xs">
                <span className="text-xs sm:text-sm text-slate-600 leading-relaxed text-center transition-colors duration-300 block font-medium">
                  {item.description}
                </span>
              </div>

              {/* Decorative bottom accent line */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${theme.iconFrom} ${theme.iconTo} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-b-2xl`} />
            </button>
          );
        })
        )}
      </main>

       {/* Footer Padding */}
       <div className="h-6 sm:h-8 flex-shrink-0"></div>

       {/* Contact Us Modal */}
       <ContactUsModal
         isOpen={isContactModalOpen}
         onClose={() => setIsContactModalOpen(false)}
         userEmail={currentUser?.email || ''}
       />
    </div>
  );
};

export default HomeScreen; 